/* Zana's fleet read, on her full page (2026-10-28, Gabriel). The briefing's
   facts are already on Overview's To do, so only its button stayed: Regenerate
   asks Zana to read the fleet again now, instead of waiting for the daily
   refresh (the production cron's 06:00 UTC window; the test server has no
   cron, so a test school's read only changes from here). A read is paid model
   calls, so the server declines one when nothing has changed since the last.
   Same reads as the classic Analytics "Zana Mode": fetchZanaBriefing for the
   date (through the backend: the rules block the browser's own aiBriefings
   read for some admins), runZanaFleetRead to regenerate, or to configure while
   Zana is still learning the fleet. Zana tier only. */
import React, { useEffect, useState } from 'react';
import { briefingAge, fetchZanaBriefing, runZanaFleetRead } from '../services/briefingsApi';
import { useToast } from './kit';

interface Brief { source?: 'ai' | 'deterministic' | 'learning' | 'configuring'; generatedAtMs?: number }

export const ZanaReadButton: React.FC<{ orgCode: string }> = ({ orgCode }) => {
  const { toast } = useToast();
  const [brief, setBrief] = useState<Brief | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!orgCode) return undefined;
    let live = true;
    setLoading(true);
    fetchZanaBriefing(orgCode)
      .then((b) => { if (live) setBrief(b as Brief | null); })
      .catch(() => undefined)
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [orgCode]);

  const configuring = brief?.source === 'configuring';
  const learning = brief?.source === 'learning' || configuring || (!brief && !loading);
  const age = learning ? null : briefingAge(brief);

  const read = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await runZanaFleetRead(orgCode, { mode: learning ? 'configure' : 'regenerate', onBrief: (b) => setBrief(b as Brief | null) });
      if (!r.ok) toast({ kind: 'error', text: r.message || 'Zana couldn’t finish reading the fleet. Try again in a minute.' });
      else if (r.result?.state === 'cached') toast({ kind: 'zana', text: 'Nothing has changed since Zana’s last read. She is up to date.' });
      else toast({ kind: 'zana', text: 'Zana has read your fleet again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="row" style={{ gap: 10, marginLeft: 'auto', flex: 'none', padding: '0 4px 4px' }}>
      {age && (
        <span className="sm desk" style={{ color: age.stale ? 'var(--warn)' : 'var(--muted)', whiteSpace: 'nowrap' }}
          title={brief?.generatedAtMs ? `Read ${new Date(brief.generatedAtMs).toLocaleString()}` : undefined}>
          {age.stale ? `Last read ${age.label} · out of date` : `Last read ${age.label}`}
        </span>
      )}
      <button type="button" className="btn s b-line" onClick={read} disabled={busy || loading || configuring}
        title="Zana reads your buses, trips, fuel and alerts again now, instead of at the morning refresh.">
        {busy || configuring
          ? <><span className="spin" aria-hidden="true" style={{ width: 14, height: 14, borderColor: 'var(--line2)', borderTopColor: 'currentColor' }} />Reading your fleet…</>
          : learning ? 'Read my fleet now' : 'Regenerate'}
      </button>
    </div>
  );
};
